import { User } from '../../services/adminApi'

interface UserListProps {
  users: User[]
  isLoading: boolean
  onResetPassword: (username: string) => void
  onDelete: (username: string) => void
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function getStatusBadge(status: string, enabled: boolean) {
  if (!enabled) {
    return (
      <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
        Disabled
      </span>
    )
  }

  switch (status) {
    case 'CONFIRMED':
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
          Active
        </span>
      )
    case 'FORCE_CHANGE_PASSWORD':
      return (
        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded">
          Password Reset Required
        </span>
      )
    default:
      return (
        <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
          {status}
        </span>
      )
  }
}

export function UserList({ users, isLoading, onResetPassword, onDelete }: UserListProps) {
  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p className="mt-2 text-gray-600">Loading users...</p>
      </div>
    )
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <p className="text-gray-500">No users found</p>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Groups
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {users.map((user) => (
            <tr key={user.username} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm font-medium text-gray-900">{user.email}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {getStatusBadge(user.status, user.enabled)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex flex-wrap gap-1">
                  {user.groups.map((group) => (
                    <span
                      key={group}
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        group === 'admin'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {group}
                    </span>
                  ))}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(user.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                <button
                  onClick={() => onResetPassword(user.username)}
                  className="text-blue-600 hover:text-blue-900"
                >
                  Reset Password
                </button>
                <button
                  onClick={() => onDelete(user.username)}
                  className="text-red-600 hover:text-red-900"
                  disabled={user.groups.includes('admin')}
                  title={user.groups.includes('admin') ? 'Cannot delete admin users' : ''}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
